// Tag 20: AnomalyStreamTile — Top-5 unbearbeitete Anomalien.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  AnomalyEventItem,
  AnomalyEventsResponse,
  AnomalySeverity,
  dismissAnomalyEvent,
  fetchAnomalyEvents,
} from "../lib/api";

import { firmaDot } from "../lib/firma-colors";

const SEVERITY_STYLE: Record<AnomalySeverity, string> = {
  info: "bg-cyan-500/15 text-cyan-200 border-cyan-500/40",
  warn: "bg-amber-500/15 text-amber-200 border-amber-500/40",
  critical: "bg-pink-500/15 text-pink-200 border-pink-500/40",
};

export default function AnomalyStreamTile() {
  const qc = useQueryClient();
  const [drawerItem, setDrawerItem] = useState<AnomalyEventItem | null>(null);

  const query = useQuery<AnomalyEventsResponse, Error>({
    queryKey: ["anomaly-events", "open"],
    queryFn: () => fetchAnomalyEvents({ status: "open", limit: 20 }),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });

  const dismissMut = useMutation({
    mutationFn: ({ id, firma_id }: { id: string; firma_id: string }) =>
      dismissAnomalyEvent(id, firma_id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["anomaly-events"] });
      setDrawerItem(null);
    },
  });

  const items = query.data?.items.slice(0, 5) ?? [];
  const nOpen = query.data?.n_open ?? 0;
  const nCritical = query.data?.n_critical_open ?? 0;

  return (
    <section className="rounded-2xl border border-pink-500/40 bg-slate-950 p-4 sm:p-5 space-y-3">
      <header className="flex items-baseline justify-between gap-2">
        <h2 className="text-[11px] font-semibold uppercase tracking-widest text-slate-100">
          Anomaly-Stream
        </h2>
        <span className="text-[10px] text-slate-400">
          {nOpen} offen
          {nCritical > 0 && (
            <span className="ml-1 text-pink-300">· {nCritical} kritisch</span>
          )}
        </span>
      </header>

      {query.isLoading && <p className="text-xs text-slate-400">Lade …</p>}

      {!query.isLoading && items.length === 0 && (
        <p className="text-xs text-slate-400 py-2">Alle Systeme normal.</p>
      )}

      {items.length > 0 && (
        <ul className="space-y-2">
          {items.map((it) => (
            <li
              key={it.id}
              className="rounded-lg border border-slate-700/60 bg-slate-950/40 p-2 flex flex-col gap-1"
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="flex items-center gap-2 text-xs text-slate-100 min-w-0">
                  <span
                    aria-hidden="true"
                    className={`inline-block w-2 h-2 rounded-full shrink-0 ${
                      firmaDot(it.firma_id)
                    }`}
                  />
                  <span className="truncate">{it.titel}</span>
                </span>
                <span
                  className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border shrink-0 ${SEVERITY_STYLE[it.severity]}`}
                >
                  {it.severity}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setDrawerItem(it)}
                className="self-start text-[10px] text-sky-300 hover:text-sky-200 min-h-9 px-1 -ml-1"
              >
                Details
              </button>
            </li>
          ))}
        </ul>
      )}

      {drawerItem && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="anomaly-drawer-title"
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 px-4 py-4"
        >
          <div className="rounded-xl border border-slate-700/60 bg-slate-950 p-4 max-w-md w-full space-y-3">
            <header className="flex items-baseline justify-between gap-2">
              <h3
                id="anomaly-drawer-title"
                className="text-sm font-semibold text-slate-100"
              >
                {drawerItem.titel}
              </h3>
              <span
                className={`text-[10px] uppercase px-2 py-0.5 rounded border ${SEVERITY_STYLE[drawerItem.severity]}`}
              >
                {drawerItem.severity}
              </span>
            </header>
            <p className="text-[11px] text-slate-400">
              {drawerItem.firma_id} · {drawerItem.kategorie} ·{" "}
              {drawerItem.detected_at.slice(0, 10)}
            </p>
            {drawerItem.payload && (
              <pre className="text-[10px] text-slate-300 bg-slate-900/60 border border-slate-700/40 rounded p-2 overflow-x-auto max-h-48">
                {JSON.stringify(drawerItem.payload, null, 2)}
              </pre>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                disabled={dismissMut.isPending}
                onClick={() =>
                  dismissMut.mutate({
                    id: drawerItem.id,
                    firma_id: drawerItem.firma_id,
                  })
                }
                className="flex-1 min-h-11 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/40 text-emerald-100 text-xs font-medium disabled:opacity-40"
              >
                ✓ Erledigt (dismiss)
              </button>
              <button
                type="button"
                onClick={() => setDrawerItem(null)}
                className="flex-1 min-h-11 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700/60 text-slate-200 text-xs"
              >
                Schliessen
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
